import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useContactActions } from 'contacts/hooks';
import { IContact } from 'contacts/types';
import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { FormProvider, useAgGridTableContext } from 'core/states';
import { corporateUserService } from 'corporate/services';
import { ICorporateUserAutocomplete } from 'corporate/types';

import { changeOwnerSchema } from './schemas';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: () => void;
}

interface IChangeOwnerForm {
  user: ICorporateUserAutocomplete;
}

export const ChangeOwner = ({ isOpen, onClose, onSubmitSuccess }: IProps) => {
  const { t } = useTranslation();

  const { selectedRows, deselectAllRows } = useAgGridTableContext();
  const { updateContactOwner } = useContactActions();

  const handleOnClose = (reset?: UseFormReset<IChangeOwnerForm>) => {
    reset?.();
    onClose();
  };

  const onSubmit = (reset: UseFormReset<IChangeOwnerForm>) => async (data: IChangeOwnerForm) => {
    const contactIds = selectedRows.map((contact: IContact) => contact.id);
    const contactGroupIds = selectedRows
      .map((contact: IContact) => contact.groupId)
      .filter((id): id is number => id !== undefined);

    const { success } = await updateContactOwner({
      corporateUserId: data.user.id,
      contactIds,
      contactGroupIds,
    });

    if (success) {
      handleOnClose(reset);
      deselectAllRows();
      onSubmitSuccess?.();
    }
  };

  return (
    <FormProvider schema={changeOwnerSchema()} defaultValues={{ user: null }}>
      <Form
        isOpen={isOpen}
        onClose={handleOnClose}
        title={t('titleChangeOwner')}
        submitBtnLabel={t('buttonLabelChangeOwner')}
        onSubmit={onSubmit}
      >
        <FormField
          name="user"
          type={InputType.AUTOCOMPLETE}
          inputProps={{
            label: t('labelSelectNewOwner'),
            placeholder: t('placeholderSelectUser'),
            getOptions: corporateUserService.getCorporateUsersAutocomplete,
            getLabel: (option: ICorporateUserAutocomplete) => option.name,
            required: true,
          }}
        />
      </Form>
    </FormProvider>
  );
};
